import { handleApiRequest, Modal, Typography, useMobile, ViewportLoading } from "@cpj-ai/panel-npm-package";
import {
  JobPositionInterviewSlotType,
  ScheduleInterviewTimeMutation,
  ScheduleInterviewTimeMutationVariables,
  useScheduleInterviewTimeMutation,
} from "@graphql/generated";
import { Button } from "@heroui/react";
import { useJobSuggestion } from "@modules/JobSuggestion/hooks/useJobSuggestion";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import DateSlider from "./components/DateSlider";
import NavigationButtons from "./components/NavigationButtons";
import TimeSlotGrid from "./components/TimeSlotGrid";

export interface SelectInterviewDateModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
}

// Type for grouped interview dates
type GroupedInterviewDates = Record<string, JobPositionInterviewSlotType[]>;

const SelectInterviewDateModal: React.FC<SelectInterviewDateModalProps> = ({ isOpen, onOpenChange, onClose }) => {
  const isMobile = useMobile();
  const { id } = useParams();
  const swiperRef = useRef<any>(null);

  // Fetch job suggestion data
  const { jobSuggestion, isLoading: isJobSuggestionLoading, refetchJobSuggestion } = useJobSuggestion(id || "");

  // Mutation for scheduling interview
  const [scheduleInterviewTime, { loading: isSchedulingInterviewTimeLoading }] = useScheduleInterviewTimeMutation();

  // State management
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<JobPositionInterviewSlotType | null>(null);

  // Get interview dates from job suggestion
  const interviewDates = jobSuggestion?.jobPosition?.interviewSlots;
  const isInterviewDatesEmpty = !interviewDates?.length;

  // Group and format interview dates by date
  const formattedInterviewDates = useMemo(() => {
    if (!interviewDates?.length) return {};

    // Group interview slots by date
    const groupedByDate = interviewDates.reduce<GroupedInterviewDates>((acc, slot) => {
      const date = slot?.datetime[0].split("T")[0];
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(slot);
      return acc;
    }, {});

    // Sort dates and time slots
    return Object.keys(groupedByDate)
      .sort()
      .reduce<GroupedInterviewDates>((sorted, date) => {
        const dateSlots = groupedByDate[date];
        if (dateSlots) {
          sorted[date] = [...dateSlots].sort((a, b) => {
            const dateA = a.datetime?.[0] || "";
            const dateB = b.datetime?.[0] || "";
            return dateA.localeCompare(dateB);
          });
        }
        return sorted;
      }, {});
  }, [interviewDates]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedDate(null);
      setSelectedTimeSlot(null);

      // Set initial date to first available date
      const availableDates = Object.keys(formattedInterviewDates);
      if (availableDates.length > 0) {
        setSelectedDate(availableDates[0] || null);
      }
    }
  }, [isOpen, formattedInterviewDates]);

  // Event handlers
  const handleSelectDate = (date: string) => {
    setSelectedDate(date);
    setSelectedTimeSlot(null);
  };

  const handleSelectTimeSlot = (timeSlot: JobPositionInterviewSlotType) => {
    setSelectedTimeSlot(timeSlot);
  };

  const handleConfirm = () => {
    if (!id || !selectedTimeSlot?.id) return;

    handleApiRequest<ScheduleInterviewTimeMutation, ScheduleInterviewTimeMutationVariables>({
      apiFunction: scheduleInterviewTime,
      variables: {
        jobPositionAssignmentId: id,
        slotId: selectedTimeSlot.id,
      },
      componentName: "SelectInterviewDateModal",
      actionName: "handleConfirm",
      successMessage: "Interview scheduled successfully",
      fallbackErrorMessage: "Failed to schedule interview. Please try again.",
      onSuccess: () => {
        onClose();
        refetchJobSuggestion();
      },
    });
  };

  // Render calendar view with date and time selection
  const renderCalendarView = () => (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Typography className="text-default-500">
          To proceed with your interview scheduling, please select your preferred date and time slot from the available
          options below. The interview will be conducted via video call.
        </Typography>
      </div>

      <NavigationButtons
        onPrevious={() => swiperRef.current?.slidePrev()}
        onNext={() => swiperRef.current?.slideNext()}
      />

      <div className="-mt-4">
        <DateSlider
          dates={Object.keys(formattedInterviewDates)}
          selectedDate={selectedDate}
          onSelectDate={handleSelectDate}
          swiperRef={swiperRef}
        />
      </div>

      {selectedDate && (
        <TimeSlotGrid
          slots={formattedInterviewDates[selectedDate] || []}
          selectedTimeSlot={selectedTimeSlot}
          onSelectTimeSlot={handleSelectTimeSlot}
        />
      )}
    </div>
  );

  // Render modal content based on state
  const renderModalContent = () => {
    if (isJobSuggestionLoading) {
      return <ViewportLoading />;
    }

    if (isInterviewDatesEmpty) {
      return (
        <div className="flex justify-center items-center h-full">
          <Typography>No interview dates found</Typography>
        </div>
      );
    }

    return renderCalendarView();
  };

  // Render footer content
  const renderFooterContent = () => {
    if (isInterviewDatesEmpty) return null;

    return (
      <div className="flex justify-end gap-2 w-full border-t border-default-200 pt-4">
        <Button variant="bordered" color="default" onPress={onClose} className="w-full md:w-auto">
          Cancel
        </Button>
        <Button
          color="primary"
          onPress={handleConfirm}
          isLoading={isSchedulingInterviewTimeLoading}
          isDisabled={!selectedDate || !selectedTimeSlot || isSchedulingInterviewTimeLoading}
          className="w-full md:w-auto"
        >
          Confirm Selection
        </Button>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onOpenChange={onOpenChange}
      label="Schedule Interview"
      size={isMobile ? "full" : "5xl"}
      classNames={{
        wrapper: "z-[1000]",
      }}
      hideCloseButton={true}
      bodyContent={renderModalContent()}
      footerContent={renderFooterContent()}
    />
  );
};

export default SelectInterviewDateModal;
